import { Injectable } from "@angular/core";
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode } from "@angular/common/http";
import { isNullOrUndefined, isValidString } from "@pineapplelab/util";
import { BrowserStorage } from "../../../models/browserStorage";
import { jwtDecode } from "jwt-decode";
import { getAuth } from "@angular/fire/auth";
import { EMPTY, Observable, catchError, from, switchMap, throwError } from "rxjs";
import { ToasterService } from "../../toaster/toaster.service";
import { AuthService } from "../auth.service";
import { environment } from "../../../../environments/environment";
import { RouteEnum } from "../../../enums/routes.enum";
import { ApiService } from "../../api/api.service";
import { GetLanguageService } from "../../translations/translations.service";
import * as Sentry from '@sentry/angular';

// Activity-tracking calls are best-effort telemetry. A rejected tracker request must never
// cost the learner their session: a 401 there used to trigger a full logout mid-chapter,
// which threw away unsaved course progress.
const QUIET_API_PATH_PREFIXES = ['/tracker/'];

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor(
	private toasterService: ToasterService,
	private authService: AuthService,
	private apiService: ApiService,
	private translationService: GetLanguageService
  ) {}

  getDecodedAccessToken(token: string): any {
    try {
      return jwtDecode(token);
    } catch(Error) {
      return null;
    }
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = BrowserStorage.get('userToken');
	if (this.shouldAppendOriginHeader(req.url)) {
		req = req.clone({
			headers: req.headers.set('x-origin', environment.hostName)
		});
	}
    if (!isValidString(token)) {
      return this.send(req, next);
    }

    const tokenInfo = this.getDecodedAccessToken(token);

    if (isNullOrUndefined(tokenInfo?.exp)) {
      return this.send(req, next);
    }

    const expirationDate = new Date(tokenInfo?.exp * 1000);

	// TODO: expirationDate should be validated by server, refactor later
    if (expirationDate <= new Date()) {
      if(isNullOrUndefined(getAuth().currentUser)){
        return next.handle(req);
      }
      return from(this.refreshToken()).pipe(
        switchMap(newToken => this.send(this.withAuthorization(req, newToken), next))
      );
    }

    return this.send(this.withAuthorization(req, token), next);
  }

  private send(req: HttpRequest<any>, next: HttpHandler, retried: boolean = false): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(catchError((error) => this.handleError(error, req, next, retried)));
  }

  private withAuthorization(req: HttpRequest<any>, token: string): HttpRequest<any> {
    if (!isValidString(token)) {
      return req;
    }
    return req.clone({
      headers: req.headers.set('authorization', token)
    });
  }

  private async refreshToken(): Promise<string> {
    const newToken = await getAuth().currentUser.getIdToken(true);
    BrowserStorage.set('userToken', newToken);
    return newToken;
  }

  handleError(error: any, req: HttpRequest<any>, next?: HttpHandler, retried: boolean = false): Observable<any> {
	const reqOptions = this.apiService.getOptions();
	const errorMessage = this.getErrorMessage(error);
  const changePasswordReq = req.url.startsWith(`${environment.API_URL}/auth/${RouteEnum.CHANGE_PASSWORD_LOGGED}`);
  const quietReq = this.isQuietRequest(req.url);

	if (error?.status === HttpStatusCode.Unauthorized && !req.url.startsWith(`${environment.API_URL}/auth/${RouteEnum.LOGIN}`) && !changePasswordReq) {
		const path = this.apiPath(req.url);
		if (quietReq) {
			Sentry.addBreadcrumb({ category: 'auth', level: 'warning', message: 'tracker request rejected with 401 (ignored)', data: { path } });
			return throwError(() => error);
		}
		// The stored token can be one the server no longer accepts while the client still believes it is
		// valid (a long-suspended tab, a device clock that runs behind). Get a fresh one and retry once
		// before treating the 401 as a dead session.
		if (!retried && next && !isNullOrUndefined(getAuth().currentUser)) {
			Sentry.addBreadcrumb({ category: 'auth', level: 'warning', message: '401 received, refreshing token and retrying', data: { path, tokenExp: this.storedTokenExpiry() } });
			return from(this.refreshToken()).pipe(
				switchMap(newToken => this.send(this.withAuthorization(req, newToken), next, true)),
				catchError((retryError) => {
					if (retryError?.status === HttpStatusCode.Unauthorized) {
						return this.handleAuthError(retryError, path, 'retry-after-refresh');
					}
					return throwError(() => retryError);
				})
			);
		}
		return this.handleAuthError(error, path, isNullOrUndefined(getAuth().currentUser) ? 'no-firebase-user' : 'already-retried');
	}
	if (isValidString(errorMessage) && reqOptions.showAPIError && !changePasswordReq && !quietReq) {
		this.toasterService.showError(errorMessage);
	}
	return throwError(() => error);
  }

  private handleAuthError(error: any, path: string = '', reason: string = ''): Observable<any> {
	const token = BrowserStorage.get('userToken');
	if (!isValidString(token)) {
		return EMPTY;
	}
	// Every forced logout is counted: this is the event that strands a learner mid-chapter.
	Sentry.captureMessage('Session ended by 401', {
		level: 'warning',
		tags: { area: 'auth', event: 'forced-logout', reason },
		extra: { path, reason, tokenExp: this.storedTokenExpiry(), serverMessage: error?.error?.message ?? null, currentUrl: location.pathname },
	});
	this.toasterService.showError(error?.error?.message ?? error?.message ?? 'general.unauthorized');
	return from(this.authService.logout());
  }

  private apiPath(url: string): string {
	return url.startsWith(environment.API_URL) ? url.slice(environment.API_URL.length) : url;
  }

  // Seconds until the stored token expires by its own claim. Negative when the client already
  // considers it expired; a positive value on a 401 means the server disagreed with the client.
  private storedTokenExpiry(): number | null {
	const exp = this.getDecodedAccessToken(BrowserStorage.get('userToken'))?.exp;
	return isNullOrUndefined(exp) ? null : Math.round(exp - Date.now() / 1000);
  }

  private isQuietRequest(url: string): boolean {
	if (!url.startsWith(environment.API_URL)) {
		return false;
	}
	const path = url.slice(environment.API_URL.length);
	return QUIET_API_PATH_PREFIXES.some(prefix => path.startsWith(prefix));
  }

  private getErrorMessage(error: any): string {
	let errorMessage = error?.error?.message ?? error?.message ?? 'general.action_error';

	if (Array.isArray(errorMessage)) {
		return errorMessage.map(message => this.translationService.getValue(`api-errors.${message}`)).join(', ');
	}
	return this.translationService.getValue(`api-errors.${errorMessage}`);
  }

  private shouldAppendOriginHeader(url: string): boolean {
	if (!url.startsWith(environment.API_URL)) {
		return false;
	}
	return location.hostname.includes('localhost');
  }
}
